import { RichText } from "@components/RichText";
import { StoryblokRichTextDocumentNode } from "@storyblok/richtext";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { twMerge } from "tailwind-merge";
import ImageCarousel from "../storyblok/ImageCarousel";
import VideoEmbed from "../storyblok/VideoEmbed";
import { VideoEmbedBlock } from "../types/VideoEmbedTypes";
import Video from "../storyblok/Video";
import { VideoBlock } from "../types/VideoTypes";
import ButtonBlock from "../storyblok/ButtonBlock";
import { ButtonBlockType } from "../types/ButtonBlockTypes";
import { Image } from "./Image";
import { ImageGallery } from "./ImageGallery";
import { ContactsBlock } from "./ContactsBlock";
import { ContactPerson } from "./ContactPerson";
import {
  ContactPersonType,
  ContactsBlockType,
} from "../types/ContactPersonTypes";
import { GridBlockType } from "../types/GridBlockTypes";
import { Grid } from "./Grid";
import { Quote, QuoteProps } from "./Quote";

interface PageProps {
  blok: SbBlokData[];
  className?: string; // Class name for the outer container
  contentClassName?: string; // Class name for the content inside the container
}

export const PageBody = ({ blok, className, contentClassName }: PageProps) => {
  if (!blok || !Array.isArray(blok)) {
    return null;
  }

  return (
    <div className={twMerge("*:last:mb-0", className ?? "")}>
      {blok.map((block) => {
        switch (block.component) {
          case "RichText":
            return (
              <div key={block._uid ?? ""} {...storyblokEditable(block)}>
                <RichText
                  className={twMerge(
                    "lg:text-lg [&_p:not(:last-child)]:mb-3 lg:[&_p:not(:last-child)]:mb-5",
                    contentClassName
                  )}
                  document={block.content as StoryblokRichTextDocumentNode}
                />
              </div>
            );

          case "Image": {
            return (
              <div key={block._uid} {...storyblokEditable(block)}>
                <Image
                  imageData={block.image}
                  isPrivateImage={!!block?.isPrivateImage}
                />
              </div>
            );
          }

          case "VideoEmbed": {
            const videoBlock = block as VideoEmbedBlock;

            return (
              <div key={videoBlock._uid} {...storyblokEditable(block)}>
                <VideoEmbed
                  videoUrl={videoBlock.videoURL ?? ""}
                  title={videoBlock.title ?? "Embedded video"}
                />
              </div>
            );
          }

          case "Video": {
            const videoBlock = block as VideoBlock;

            return (
              <div key={videoBlock._uid} {...storyblokEditable(block)}>
                <Video
                  video={videoBlock.video ?? ""}
                  title={videoBlock.title ?? "Self-hosted video"}
                  poster={videoBlock.poster}
                  captions={videoBlock.captions}
                />
              </div>
            );
          }

          case "ImageCarousel": {
            const images = Array.isArray(block.images)
              ? block.images.map(
                  (
                    img: { filename: string; alt: string; name?: string },
                    index: number
                  ) => ({
                    id: index,
                    image: img.filename,
                    alt: img.alt,
                    caption: img?.name,
                  })
                )
              : [];

            if (images.length === 0) return null;

            return (
              <div key={block._uid} {...storyblokEditable(block)}>
                <ImageCarousel images={images} />
              </div>
            );
          }

          case "ImageGallery": {
            const images = Array.isArray(block.images)
              ? block.images.map(
                  (img: { filename: string; alt: string }, index: number) => ({
                    id: index,
                    image: img.filename,
                    alt: img.alt,
                  })
                )
              : [];

            if (images.length === 0) return null;

            return (
              <div key={block._uid} {...storyblokEditable(block)}>
                <ImageGallery
                  images={images}
                  rowCount={block.rowCount as string}
                />
              </div>
            );
          }

          case "Button": {
            const buttonBlock = block as ButtonBlockType;

            if (!buttonBlock._uid || !buttonBlock.title || !buttonBlock.link) {
              return null;
            }

            return (
              <div
                key={buttonBlock._uid}
                className={contentClassName ?? ""}
                {...storyblokEditable(block)}
              >
                <ButtonBlock
                  blockKey={buttonBlock._uid}
                  title={buttonBlock.title}
                  link={buttonBlock.link}
                />
              </div>
            );
          }

          case "ContactsBlock": {
            const contactsBlock = block as ContactsBlockType;

            return (
              <div key={contactsBlock._uid} {...storyblokEditable(block)}>
                <ContactsBlock
                  title={contactsBlock?.title}
                  description={contactsBlock?.description}
                  contacts={contactsBlock?.contacts}
                />
              </div>
            );
          }

          case "ContactPerson": {
            const contactPersonBlock = block as ContactPersonType;

            return (
              <div key={contactPersonBlock._uid} {...storyblokEditable(block)}>
                <ContactPerson email={contactPersonBlock.emailAddress} />
              </div>
            );
          }

          case "Grid": {
            const gridBlock = block as GridBlockType;

            return (
              <div key={gridBlock._uid} {...storyblokEditable(block)}>
                <Grid columns={gridBlock.columns} body={gridBlock.body} />
              </div>
            );
          }

          case "Quote": {
            const quoteBlock = block as SbBlokData & QuoteProps;

            return (
              <div key={quoteBlock._uid} {...storyblokEditable(block)}>
                <Quote content={quoteBlock.content} name={quoteBlock.name} />
              </div>
            );
          }

          default:
            return null;
        }
      })}
    </div>
  );
};
